import { type FC, useCallback, useEffect } from 'react';

import { Pagination } from '@vakantiesnl/components/src/molecules/Pagination';
import { ProductCardWrapper } from '@vakantiesnl/components/src/molecules/ProductCard/ProductCardWrapper';
import { getGeoEntitiesFromSlugs } from '@vakantiesnl/components/src/utils/geo';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { useMapGeo } from '@vakantiesnl/services/src/gtm/util/useMapGeo';
import { useGetFlashDeals, useGetGeo } from '@vakantiesnl/services/src/hooks/queries';
import { useGetCompositeSearchParams } from '@vakantiesnl/services/src/hooks/useGetCompositeSearchParams';
import { DEFAULT_SEARCH_RESULTS_SIZE, useFilterStore } from '@vakantiesnl/services/src/stores/filtersStore';
import { DEFAULT_PARTY } from '@vakantiesnl/services/src/util/partyUtils';
import { useRouter } from 'next/router';

import { useStyles } from '../AccommodationList/AccommodationList.style';
import { AccommodationListSkeleton } from '../AccommodationList/AccommodationListSkeleton';

const DEALS_PAGE_SIZE = 10;

export const FlashDealSearchList: FC = () => {
	const microCopies = useMicroCopyContext();
	const router = useRouter();

	const geo = useGetGeo();

	const filters = useFilterStore((s) => s.filters);
	const noOfFilters = useFilterStore((s) => s.getSelectedFilterCount());
	const setPage = useFilterStore((s) => s.setPage);

	const params = useGetCompositeSearchParams();

	const { flashDeals, flashDealsTotal, isFlashDealsLoading } = useGetFlashDeals({
		...params,
		page_size: DEALS_PAGE_SIZE,
		page: filters.page,
		rooms: DEFAULT_PARTY,
		party: DEFAULT_PARTY[0],
	});

	const { country, region, city } = getGeoEntitiesFromSlugs(filters, geo);

	const { classes } = useStyles();

	const setPageFn = useCallback(
		(value: number) => {
			const qp = new URLSearchParams(router.asPath.split('?')[1]);

			/** Clear the default page query */
			if (value === 1) {
				qp.delete('page');
			} else {
				qp.set('page', value.toString());
			}

			router.push(
				{
					pathname: country[0]?.slug ? `/${country[0]?.slug}/deals` : `/deals`,
					query: qp.toString(),
				},
				undefined,
				{ shallow: true },
			);
			setPage(value);
		},
		[country, setPage, router],
	);

	const GTMformatedGeo = useMapGeo({
		country: country?.map((country) => country.name).toString(),
		region: region?.map((region) => region.name).toString(),
		city: city?.map((city) => city.name).toString(),
	});

	useEffect(() => {
		if (flashDeals && !isFlashDealsLoading) {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/search').then(({ searchPageView }) => {
					track(
						searchPageView(
							flashDealsTotal,
							filters.page,
							DEFAULT_SEARCH_RESULTS_SIZE,
							'popularity',
							flashDeals.map(({ accommodation }) => String(accommodation.giataId)),
							filters as unknown as Record<string, unknown>,
							{
								entity_level: 'destination',
								entity_id: '',
							},
							GTMformatedGeo,
							noOfFilters,
						),
					);
				}),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isFlashDealsLoading]);

	/** Show skeleton then AccoList is loading */
	if (isFlashDealsLoading) return <AccommodationListSkeleton />;

	/** When no results show the EmptyState component */
	if (!isFlashDealsLoading && flashDeals.length === 0) return <h1>{microCopies['accommodation.noresults.title']}</h1>;

	return (
		<>
			{flashDeals.map(({ accommodation, flashDeal }, index) => (
				<div className={classes.accoTile} key={accommodation.title}>
					<ProductCardWrapper
						key={`${index}-${accommodation.title}`}
						accommodation={accommodation}
						flashDeal={flashDeal}
						position={index}
						pageType="listing"
						size="large"
						needsDurationOverride
					/>
				</div>
			))}
			<Pagination total={flashDealsTotal} pageSize={DEALS_PAGE_SIZE} page={filters.page} setPageFn={setPageFn} />
		</>
	);
};
